import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getApiEndpoint } from '../config/api';

const AdminPropertyForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;

    const initialFormData = {
        title: '',
        description: '',
        price: '',
        type: 'PG',
        location: '',
        image_url: '',
        bedrooms: 1,
        bathrooms: 1
    };

    const [formData, setFormData] = useState(initialFormData);
    const [imageFile, setImageFile] = useState(null); // Main image
    const [mediaList, setMediaList] = useState([]); // Gallery media
    const [amenities, setAmenities] = useState([]); // Property amenities
    const [newAmenity, setNewAmenity] = useState(''); // Input for adding new amenity
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            fetchProperty();
        } else {
            // Reset form when switching to Add mode
            setFormData(initialFormData);
            setImageFile(null);
            setMediaList([]);
            setAmenities([]);
        }
    }, [isEditMode, id]);

    const fetchProperty = async () => {
        try {
            const response = await axios.get(getApiEndpoint(`get_property_details.php?id=${id}`));
            const data = response.data;
            setFormData({
                title: data.title,
                description: data.description,
                price: data.price,
                type: data.type,
                location: data.location,
                image_url: data.image_url,
                bedrooms: data.bedrooms,
                bathrooms: data.bathrooms
            });
            if (data.media) {
                setMediaList(data.media);
            }
            // Load amenities
            if (data.amenities && Array.isArray(data.amenities)) {
                setAmenities(data.amenities);
            }
        } catch (error) {
            console.error("Error fetching property details", error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Main Image Upload (Featured)
    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const formData = new FormData();
            formData.append('image', file);
            setUploading(true);
            try {
                const response = await axios.post(getApiEndpoint('upload_image.php'), formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                if (response.data.success) {
                    setFormData(prev => ({ ...prev, image_url: response.data.url }));
                }
            } catch (error) {
                console.error("Image upload failed", error);
                alert("Image upload failed");
            } finally {
                setUploading(false);
            }
        }
    };

    // Gallery Upload (Multiple)
    const handleGalleryUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setUploading(true);
        try {
            for (const file of files) {
                const uploadData = new FormData();
                uploadData.append('image', file);

                // 1. Upload file
                const uploadRes = await axios.post(getApiEndpoint('upload_image.php'), uploadData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                if (uploadRes.data.success) {
                    // 2. Link to property if in edit mode (we need property ID)
                    if (isEditMode) {
                        await axios.post(getApiEndpoint('property_media.php'), {
                            property_id: id,
                            media_type: uploadRes.data.type,
                            media_url: uploadRes.data.url
                        });
                    } else {
                        // If adding new property, we can't link media yet. 
                        // For simplicity in this iteration, we recommend saving property first, then adding media.
                        // Or we could store in temp state and upload after save.
                        // Let's prompt user to save first or handle it by just warning.
                        // For now, let's just alert if no ID.
                        alert("Please save the property first before adding gallery images.");
                        setUploading(false);
                        return;
                    }
                }
            }
            // Refresh list
            fetchProperty();
        } catch (error) {
            console.error("Gallery upload failed", error);
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteMedia = async (mediaId) => {
        if (!confirm("Are you sure you want to delete this file?")) return;
        try {
            await axios.delete(getApiEndpoint(`property_media.php?id=${mediaId}`));
            setMediaList(prev => prev.filter(item => item.id !== mediaId));
        } catch (error) {
            console.error("Delete failed", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = isEditMode ? { ...formData, id, amenities } : { ...formData, amenities };
            const method = isEditMode ? 'put' : 'post';
            // axios.put uses body as second arg, same as post
            await axios({
                method: method,
                url: getApiEndpoint('admin_properties.php'),
                data: payload
            });
            // If just created, we might want to stay on edit page to add media? 
            // Standard flow: go to dashboard.
            navigate('/admin/dashboard');
        } catch (error) {
            console.error("Error saving property", error);
            alert("Failed to save property");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-form-container">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">{isEditMode ? 'Edit Property' : 'Add New Property'}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className="admin-label">Title</label>
                    <input type="text" name="title" value={formData.title} onChange={handleChange} required className="admin-input" />
                </div>

                <div className="admin-form-row">
                    <div>
                        <label className="admin-label">Type</label>
                        <select name="type" value={formData.type} onChange={handleChange} className="admin-select">
                            <option value="PG">PG</option>
                            <option value="Flat">Flat</option>
                        </select>
                    </div>
                    <div>
                        <label className="admin-label">Price (₹)</label>
                        <input type="number" name="price" value={formData.price} onChange={handleChange} required className="admin-input" />
                    </div>
                </div>

                <div>
                    <label className="admin-label">Location</label>
                    <input type="text" name="location" value={formData.location} onChange={handleChange} required className="admin-input" />
                </div>

                <div>
                    <label className="admin-label">Description</label>
                    <textarea name="description" value={formData.description} onChange={handleChange} rows="4" className="admin-textarea"></textarea>
                </div>

                <div className="admin-form-row">
                    <div>
                        <label className="admin-label">Bedrooms</label>
                        <input type="number" name="bedrooms" value={formData.bedrooms} onChange={handleChange} className="admin-input" />
                    </div>
                    <div>
                        <label className="admin-label">Bathrooms</label>
                        <input type="number" name="bathrooms" value={formData.bathrooms} onChange={handleChange} className="admin-input" />
                    </div>
                </div>

                {/* Amenities Section */}
                <div className="admin-form-section">
                    <h3 className="admin-form-section-title">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 11H3v2h6v-2zm0 4H3v2h6v-2zm0-8H3v2h6V7zm1 14h12V3H10v18z" />
                        </svg>
                        Amenities
                    </h3>

                    <div style={{ marginBottom: '1rem' }}>
                        <label className="admin-label">Add Amenities</label>
                        <div className="admin-amenity-input-group" style={{ display: 'flex', gap: '0.75rem' }}>
                            <input
                                type="text"
                                value={newAmenity}
                                onChange={(e) => setNewAmenity(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        if (newAmenity.trim()) {
                                            setAmenities([...amenities, newAmenity.trim()]);
                                            setNewAmenity('');
                                        }
                                    }
                                }}
                                placeholder="e.g., WiFi, AC, Parking"
                                className="admin-input"
                                style={{ flex: 1 }}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    if (newAmenity.trim()) {
                                        setAmenities([...amenities, newAmenity.trim()]);
                                        setNewAmenity('');
                                    }
                                }}
                                className="admin-btn-primary"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                Add Amenity
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Press Enter or click button to add</p>
                    </div>

                    {/* List of Selected Amenities */}
                    {amenities.length > 0 && (
                        <div className="admin-amenities-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
                            {amenities.map((amenity, index) => (
                                <div
                                    key={index}
                                    className="admin-amenity-tag"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.5rem 0.75rem',
                                        backgroundColor: '#ecfdf5',
                                        border: '1px solid #6ee7b7',
                                        borderRadius: '0.5rem',
                                        fontSize: '0.875rem',
                                        color: '#065f46'
                                    }}
                                >
                                    {amenity}
                                    <button
                                        type="button"
                                        onClick={() => setAmenities(amenities.filter((_, i) => i !== index))}
                                        className="admin-amenity-remove-btn"
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#dc2626',
                                            cursor: 'pointer',
                                            fontSize: '1.25rem',
                                            lineHeight: 1,
                                            padding: 0
                                        }}
                                        title="Remove amenity"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div>
                    <label className="admin-label">Main Image (Thumbnail)</label>
                    <input type="file" onChange={handleImageChange} accept="image/*" className="mt-1 block w-full" />
                    {uploading && <p className="text-sm text-blue-500 mt-1">Uploading...</p>}
                    {formData.image_url && (
                        <div className="mt-2">
                            <img src={formData.image_url} alt="Preview" className="admin-image-preview" />
                        </div>
                    )}
                </div>

                {/* Media Gallery Section - Only in Edit Mode */}
                {isEditMode && (
                    <div className="border-t pt-6 mt-6">
                        <h3 className="text-lg font-semibold mb-4 text-gray-700">Media Gallery (Images & Videos)</h3>


                        <div className="admin-gallery-grid">
                            {mediaList.map((media) => (
                                <div key={media.id} className="admin-gallery-item">
                                    {media.media_type === 'video' ? (
                                        <video src={media.media_url} className="admin-gallery-media" controls />
                                    ) : (
                                        <img src={media.media_url} alt="Gallery" className="admin-gallery-media" />
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteMedia(media.id)}
                                        className="admin-delete-btn"
                                        title="Delete"
                                    >
                                        &times;
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div>
                            <label className="admin-label">Add Gallery Media</label>
                            <input
                                type="file"
                                multiple
                                onChange={handleGalleryUpload}
                                accept="image/*,video/*"
                                className="mt-1 block w-full"
                            />
                            <p className="text-sm text-gray-500 mt-1">Select multiple files (Images or Videos).</p>
                        </div>
                    </div>
                )}
                {!isEditMode && (
                    <div className="bg-yellow-50 p-4 rounded text-yellow-800 text-sm">
                        Note: Save the property first to add more gallery images/videos.
                    </div>
                )}


                <div className="admin-form-actions">
                    <button type="button" onClick={() => navigate('/admin/dashboard')} className="admin-btn-secondary">Cancel</button>
                    <button type="submit" disabled={loading || uploading} className="admin-btn-primary">
                        {loading ? 'Saving...' : 'Save Property'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminPropertyForm;
